// Working-hours threshold resolution.
//
// A device carries two threshold sets: the top-level one (the non-working/default
// set) and a nested `workingHours` set that applies only inside a configured
// window. Everything that judges or displays a *live* reading has to ask which set
// is in force at this instant — see AlarmConfigForm for how the pair is saved.

const DAY_MINUTES = 24 * 60;

// Parse the stored "HH:mm" into minutes from midnight. Returns null on anything
// unparseable so a malformed config falls back to the non-working set rather than
// silently judging against a garbage window.
const parseClock = (raw: any) => {
    const match = String(raw ?? '')
        .trim()
        .match(/^(\d{1,2})\s*:?\s*(\d{2})$/);
    if (!match) return null;
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (hour > 24 || minute > 59) return null;
    return Math.min(DAY_MINUTES, hour * 60 + minute);
};

// Minutes-from-midnight and weekday (0=Sun) for `date` as seen in `timezone`. The
// window is configured in the device's local zone, so evaluating it in the
// browser's zone would shift the boundaries for anyone travelling or on a device
// set to another region. Falls back to the browser's zone if the runtime rejects
// the stored zone name.
const zonedParts = (date: Date, timezone: any) => {
    const format = (zone: any) =>
        new Intl.DateTimeFormat('en-US', {
            timeZone: zone,
            hour12: false,
            weekday: 'short',
            hour: '2-digit',
            minute: '2-digit',
        }).formatToParts(date);

    let parts;
    try {
        parts = format(timezone);
    } catch {
        parts = format(undefined);
    }

    const pick = (type: string) => parts.find((p: any) => p.type === type)?.value;
    // Intl renders midnight as "24" under hour12:false in some runtimes; fold it
    // back to 0 so the comparison below stays inside a single day.
    const hour = Number(pick('hour')) % 24;
    const minute = Number(pick('minute'));
    const weekdayIndex = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(pick('weekday') ?? '');

    return { minutes: hour * 60 + minute, weekday: weekdayIndex };
};

const isWorkingDay = (weekday: number, days: string) => (days === 'everyday' ? true : weekday >= 1 && weekday <= 5);

/**
 * Whether `date` falls inside the configured working window.
 *
 * Overnight windows (end <= start, e.g. 22:00–06:00) belong to the day they
 * *start* on: 02:00 Saturday is still part of Friday's late shift, so a weekdays
 * schedule counts it as working time.
 */
export const isWithinWorkingHours = (workingHours: any, date: Date = new Date()) => {
    if (!workingHours?.enabled) return false;

    const start = parseClock(workingHours.startTime);
    const end = parseClock(workingHours.endTime);
    if (start === null || end === null) return false;

    const { minutes, weekday } = zonedParts(date, workingHours.timezone);
    if (weekday < 0) return false;

    const days = workingHours.days;

    // Same-day window: both endpoints sit on today.
    if (end > start) {
        return minutes >= start && minutes < end && isWorkingDay(weekday, days);
    }

    // Wrapped window. Before `end` we're in the tail of yesterday's shift; at or
    // after `start` we're in the head of today's.
    if (minutes < end) {
        const previousDay = (weekday + 6) % 7;
        return isWorkingDay(previousDay, days);
    }
    if (minutes >= start) {
        return isWorkingDay(weekday, days);
    }
    return false;
};

/**
 * The upper/lower/time limits in force at `date`, plus which set won.
 *
 * `record` is a mapped realtime record: its top-level limits are the non-working
 * set. Falls back to the non-working value for any field the working set omits,
 * so a half-configured working set can't blank a limit on the card.
 */
export const resolveActiveThreshold = (record: any, date: Date = new Date()) => {
    const working = record?.workingHours;
    const active = isWithinWorkingHours(working, date);

    if (!active) {
        return {
            upperLimit: record?.upperLimit,
            lowerLimit: record?.lowerLimit,
            timeLimit: record?.timeLimit,
            isWorkingHours: false,
        };
    }

    return {
        upperLimit: working.upperLimit ?? record.upperLimit,
        lowerLimit: working.lowerLimit ?? record.lowerLimit,
        timeLimit: working.timeLimit ?? record.timeLimit,
        isWorkingHours: true,
    };
};
